import React, { useEffect, useMemo, useRef, useState } from "react";
import { useOutletContext, useNavigate, useLocation } from "react-router-dom";
import spinnerIcon from "../assets/img/loadingSpinner.gif";
import { apiGet } from "../services/AuthService.js";
import { useAuth0 } from "@auth0/auth0-react";

import MembershipCarousel from "../components/membership/MembershipCarousel.js";

const safeApiGet = async (apiUrl, getAccessTokenSilently) => {
    const response = await apiGet(apiUrl, getAccessTokenSilently);
    return response;
};

const completeCheckoutAndUpgradeNew = async (accountId, checkoutSessionId, getAccessTokenSilently) => {
    const apiUrl = `a_payments/completeCheckoutAndUpgrade-new?accountId=${accountId}&checkoutSessionId=${checkoutSessionId}`;
    const response = await safeApiGet(apiUrl, getAccessTokenSilently);
    return { success: Boolean(response?.ok), status: response?.status ?? 0 };
};

const parseCheckoutReturn = (search, state) => {
    const params = new URLSearchParams(search || "");
    const sessionIdFromQuery = params.get("session_id") || "";

    const checkoutSessionId = state?.checkoutSessionId || sessionIdFromQuery || "";
    const celebrateFromState = Boolean(state?.celebrate);

    return { checkoutSessionId, celebrateFromState };
};

const EmojiConfettiOverlay = ({ runKey }) => {
    const pieces = useMemo(() => {
        if (!runKey) return [];
        const emojis = ["🎉", "✨", "🎊", "💥", "⭐"];
        const count = 42;

        const next = [];
        for (let i = 0; i < count; i += 1) {
            const left = Math.random() * 100;
            const delay = Math.random() * 0.35;
            const duration = 1.6 + Math.random() * 1.0;
            const size = 14 + Math.round(Math.random() * 16);
            const emoji = emojis[Math.floor(Math.random() * emojis.length)];
            next.push({ key: `${runKey}-${i}`, left, delay, duration, size, emoji });
        }
        return next;
    }, [runKey]);

    if (!runKey) return null;

    return (
        <div className="td-confetti" aria-hidden="true">
            <style>{`
                @media (prefers-reduced-motion: reduce) {
                    .td-confetti { display: none; }
                }

                .td-confetti{
                    position: fixed;
                    inset: 0;
                    pointer-events: none;
                    z-index: 9999;
                    overflow: hidden;
                }

                .td-confetti__piece{
                    position: absolute;
                    top: -12vh;
                    will-change: transform;
                    animation-name: td-confetti-fall;
                    animation-timing-function: ease-in;
                    animation-fill-mode: both;
                }

                @keyframes td-confetti-fall{
                    0%{ transform: translate3d(0, -10vh, 0) rotate(0deg); opacity: 0; }
                    10%{ opacity: 1; }
                    100%{ transform: translate3d(0, 120vh, 0) rotate(520deg); opacity: 0; }
                }
            `}</style>

            {pieces.map((p) => (
                <span
                    key={p.key}
                    className="td-confetti__piece"
                    style={{
                        left: `${p.left}%`,
                        fontSize: `${p.size}px`,
                        animationDuration: `${p.duration}s`,
                        animationDelay: `${p.delay}s`,
                    }}
                >
                    {p.emoji}
                </span>
            ))}
        </div>
    );
};

const MembershipPlans = () => {
    const { getAccessTokenSilently } = useAuth0();
    const outletValues = useOutletContext();

    // Outlet tuple shape is preserved; only index 0 and 4 are used.
    const currentAccount = outletValues?.[0] ?? null;
    const accountProfile = outletValues?.[1] ?? null;
    const setIsShowSidebarMd = outletValues?.[4] ?? null;

    const navigate = useNavigate();
    const location = useLocation();

    const [pageStatus, setPageStatus] = useState("loading"); // loading | ready | error
    const [pageError, setPageError] = useState("");

    const [celebrateKey, setCelebrateKey] = useState(0);
    const confettiTimerRef = useRef(null);

    const processedCheckoutSessionIdRef = useRef(null);
    const checkoutCompletionInFlightRef = useRef(false);

    const checkoutReturn = useMemo(() => parseCheckoutReturn(location.search, location.state), [location.search, location.state]);

    useEffect(() => {
        if (typeof setIsShowSidebarMd === "function") setIsShowSidebarMd(true);
    }, [setIsShowSidebarMd]);

    useEffect(() => {
        const key = celebrateKey;
        if (!key) return;

        const durationMs = 2000;
        if (confettiTimerRef.current) window.clearTimeout(confettiTimerRef.current);
        confettiTimerRef.current = window.setTimeout(() => setCelebrateKey(0), durationMs);

        return () => {
            if (confettiTimerRef.current) window.clearTimeout(confettiTimerRef.current);
        };
    }, [celebrateKey]);

    useEffect(() => {
        (async () => {
            const accountId = currentAccount?.id ?? 0;
            if (!accountId) return;

            const checkoutSessionId = checkoutReturn.checkoutSessionId || null;
            if (!checkoutSessionId) {
                setPageStatus("ready");
                return;
            }

            if (processedCheckoutSessionIdRef.current === checkoutSessionId) return;
            if (checkoutCompletionInFlightRef.current) return;

            checkoutCompletionInFlightRef.current = true;
            processedCheckoutSessionIdRef.current = checkoutSessionId;

            try {
                setPageStatus("loading");
                setPageError("");

                // Clear route state/query to avoid replay on refresh/back.
                navigate(location.pathname, { replace: true, state: null });

                const result = await completeCheckoutAndUpgradeNew(accountId, checkoutSessionId, getAccessTokenSilently);

                if (!result.success) {
                    setPageStatus("error");
                    setPageError(`Failed to finalize checkout. (HTTP ${result.status})`);
                    return;
                }

                const celebrateFromSession = (() => {
                    try {
                        return sessionStorage.getItem("totdata_celebrate") === "1";
                    } catch {
                        return false;
                    }
                })();

                const shouldCelebrate = checkoutReturn.celebrateFromState || celebrateFromSession;

                if (shouldCelebrate) {
                    setCelebrateKey(Date.now());
                    try {
                        sessionStorage.removeItem("totdata_celebrate");
                    } catch { }
                }

                setPageStatus("ready");
            } catch (ex) {
                setPageStatus("error");
                setPageError(ex?.message ?? "Unknown error.");
            } finally {
                checkoutCompletionInFlightRef.current = false;
            }
        })();
    }, [currentAccount?.id, checkoutReturn.checkoutSessionId, checkoutReturn.celebrateFromState, getAccessTokenSilently, location.pathname, navigate]);

    const persistedCurrentAccount = (() => {
        try {
            return JSON.parse(localStorage.getItem("currentAccount") ?? "null");
        } catch {
            return null;
        }
    })();

    const persistedAccountProfile = (() => {
        try {
            return JSON.parse(localStorage.getItem("accountProfile") ?? "null");
        } catch {
            return null;
        }
    })();

    const effectiveAccountProfile = accountProfile ?? persistedAccountProfile ?? null;

    const viewerCanManageMembershipUi = Boolean(
        effectiveAccountProfile?.isAdmin ||
        effectiveAccountProfile?.hasImpersonate ||
        false
    );

    const showLoading = () => (
        <div>
            <img src={spinnerIcon} alt="loading..." />
        </div>
    );

    const showError = () => (
        <div>
            Oops, there was a page load error: {pageError}
        </div>
    );

    const accountId = currentAccount?.id ?? 0;

//    const membershipEnvironmentEnabled = isMembershipEnvironmentEnabled(baseUrl);

 //   if (!membershipEnvironmentEnabled) return null;

    return (
        <div>
            <h4 style={{ textAlign: 'center', marginBottom: '0' }}>Membership Plans</h4>

            <EmojiConfettiOverlay runKey={celebrateKey} />

            {(() => {
                if (!accountId) return showLoading();
                if (pageStatus === "loading") return showLoading();
                if (pageStatus === "error") return showError();
                return (
                    <>
                        <MembershipCarousel accountId={accountId} celebrateKey={celebrateKey} />
                        <p style={{ textAlign: 'center', fontWeight: 'bold', fontStyle: 'italic', fontSize: '1.1rem', margin: '16px 0' }}>
                            *Document Conversion not Included in Membership Plans*
                        </p>
                    </>
                );
            })()}
        </div>
    );
};

export default MembershipPlans;
